import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, Drawer, Grid, Header } from '~/components/blocks';
import {
  AddOrganizationAddressDocument,
  AddOrganizationContactPointDocument,
  GetOrganizationDocument,
  OrganizationBaseFragment,
  Practitioner,
  UpdateOrganizationAddressDocument,
  UpdateOrganizationContactPointDocument,
  UpdateOrganizationDocument,
} from '~/graphql/generated/graphql';
import { useScrollToTop } from '~/hooks/use-scroll-to-top';
import { editBaseDrawerState } from '~/state/settings_organization_base/atoms';

import { BaseForm } from '../BaseForm';
import { Fields } from '../BaseForm/types';
import { formatOrganizationBase } from '../formatter';
import { useFetchLatestUberSignature } from '../use-fetch_latest_uber_signature';
import { useFetchUberOrganizationSetting } from '../use-fetch-uber_organization_setting';

const formatOrganizationBaseDataWithId = (loginPractitioner: Practitioner) => {
  const organization = formatOrganizationBase(loginPractitioner);
  const organizationData = loginPractitioner.organization as OrganizationBaseFragment;
  const address = organizationData.organizationAddresses[0];
  const contactPoints = organizationData.organizationContactPoints;
  const fax = contactPoints.find(
    ({ contactPoint }) => contactPoint.use === 'work' && contactPoint.system === 'fax',
  );
  const tel = contactPoints.find(
    ({ contactPoint }) => contactPoint.use === 'work' && contactPoint.system === 'phone',
  );

  return {
    ...organization,
    address: { ...organization.address, id: address?.id, position: address?.position },
    contactPoints: {
      fax: { id: fax?.id, position: fax?.position },
      tel: { id: tel?.id, position: tel?.position },
    },
  };
};

export const EditBaseDrawer = () => {
  const theme = useTheme();
  const [getOrganization, { data }] = useLazyQuery(GetOrganizationDocument, {
    fetchPolicy: 'no-cache',
  });
  const { uberSettingEnable } = useFetchUberOrganizationSetting();
  const { availableUber } = useFetchLatestUberSignature();
  const organization = useMemo(
    () => (data?.me ? formatOrganizationBaseDataWithId(data.me as Practitioner) : null),
    [data?.me],
  );
  const [containerRef, scrollToTop] = useScrollToTop<HTMLDivElement>();
  const [state, setDrawerState] = useAtom(editBaseDrawerState);
  const [addAddress, { loading: addAddressLoading, error: addAddressError }] = useMutation(
    AddOrganizationAddressDocument,
    {
      refetchQueries: [{ query: GetOrganizationDocument }],
    },
  );
  const [addContactPoint, { loading: addContactPointLoading, error: addContactPointError }] =
    useMutation(AddOrganizationContactPointDocument);
  const [updateAddress, { loading: updateAddressLoading, error: updateAddressError }] = useMutation(
    UpdateOrganizationAddressDocument,
  );
  const [
    updateContactPoint,
    { loading: updateContactPointLoading, error: updateContactPointError },
  ] = useMutation(UpdateOrganizationContactPointDocument);
  const [
    updateOrganization,
    { loading: updateOrganizationLoading, error: updateOrganizationError },
  ] = useMutation(UpdateOrganizationDocument);
  const loading =
    updateOrganizationLoading ||
    addAddressLoading ||
    addContactPointLoading ||
    updateAddressLoading ||
    updateContactPointLoading;

  const formRef = useRef<RefAttributeType<typeof BaseForm>>(null);

  const initialValue = useMemo<Fields>(
    () => ({
      name: organization?.name || '',
      pharmacyCode: organization?.pharmacyCode || '',
      city: organization?.address.city || '',
      line: organization?.address.line || '',
      postalCode: organization?.address.postalCode || '',
      prefecture: organization?.address.prefecture || '',
      tel: organization?.tel || '',
      fax: organization?.fax || '',
      latitude: organization?.address.latitude || 0,
      longitude: organization?.address.longitude || 0,
    }),
    [
      organization?.address?.city,
      organization?.address?.latitude,
      organization?.address?.line,
      organization?.address?.longitude,
      organization?.address?.postalCode,
      organization?.address?.prefecture,
      organization?.fax,
      organization?.name,
      organization?.pharmacyCode,
      organization?.tel,
    ],
  );
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    setDisabled(initialValue.latitude === 0 && initialValue.longitude === 0);
  }, [initialValue.latitude, initialValue.longitude]);

  const handleClickGeocoding = useCallback(() => {
    if (!formRef.current) return;

    if (!(formRef.current.errors.latitude || formRef.current.errors.longitude)) {
      setDisabled(false);
    }
  }, []);
  const handelClose = useCallback(
    () => setDrawerState(() => ({ isOpen: false })),
    [setDrawerState],
  );
  const handleSubmit = useCallback(
    async (values: Fields) => {
      if (!organization) return;

      setDrawerState((state) => ({
        ...state,
        addressError: undefined,
        contactPointError: undefined,
        organizationError: undefined,
      }));

      try {
        const promises: Promise<unknown>[] = [
          updateOrganization({
            variables: {
              input: {
                organizationId: organization.id,
                name: values.name,
                pharmacyCode: values.pharmacyCode,
              },
            },
          }),
        ];

        if (organization.address.id) {
          promises.push(
            updateAddress({
              variables: {
                input: {
                  organizationAddressId: organization.address.id,
                  position: organization.address.position,
                  address: {
                    city: values.city,
                    line: values.line,
                    postalCode: values.postalCode,
                    prefecture: values.prefecture,
                    latitude: values.latitude,
                    longitude: values.longitude,
                  },
                },
              },
            }),
          );
        } else {
          promises.push(
            addAddress({
              variables: {
                input: {
                  organizationId: organization.id,
                  city: values.city,
                  line: values.line,
                  postalCode: values.postalCode,
                  prefecture: values.prefecture,
                  latitude: values.latitude,
                  longitude: values.longitude,
                },
              },
            }),
          );
        }

        if (organization.contactPoints.tel.id && organization.contactPoints.tel.position) {
          promises.push(
            updateContactPoint({
              variables: {
                input: {
                  organizationContactPointId: organization.contactPoints.tel.id,
                  position: organization.contactPoints.tel.position,
                  contactPoint: {
                    system: 'phone',
                    value: values.tel,
                  },
                },
              },
            }),
          );
        } else {
          promises.push(
            addContactPoint({
              variables: {
                input: {
                  organizationId: organization.id,
                  system: 'phone',
                  value: values.tel,
                },
              },
            }),
          );
        }

        if (organization.contactPoints.fax.id && organization.contactPoints.fax.position) {
          promises.push(
            updateContactPoint({
              variables: {
                input: {
                  organizationContactPointId: organization.contactPoints.fax.id,
                  position: organization.contactPoints.fax.position,
                  contactPoint: {
                    system: 'fax',
                    value: values.fax,
                  },
                },
              },
            }),
          );
        } else {
          promises.push(
            addContactPoint({
              variables: {
                input: {
                  organizationId: organization.id,
                  system: 'fax',
                  value: values.fax,
                },
              },
            }),
          );
        }

        await Promise.all(promises);
        setDrawerState({ isOpen: false });
      } catch {
        // 何もしない
      }
    },
    [
      addAddress,
      addContactPoint,
      organization,
      setDrawerState,
      updateAddress,
      updateContactPoint,
      updateOrganization,
    ],
  );
  const handleSend = useCallback(() => {
    if (!formRef.current) return;
    formRef.current.submitForm();
  }, []);

  useEffect(() => {
    if (state.isOpen) {
      getOrganization();
    }
  }, [getOrganization, state.isOpen]);

  useEffect(() => {
    if (addAddressError) {
      setDrawerState((state) => {
        return { ...state, addressError: addAddressError.message };
      });
    }
  }, [addAddressError, setDrawerState]);

  useEffect(() => {
    if (addContactPointError) {
      setDrawerState((state) => {
        return { ...state, contactPointError: addContactPointError.message };
      });
    }
  }, [addContactPointError, setDrawerState]);

  useEffect(() => {
    if (updateAddressError) {
      setDrawerState((state) => {
        return { ...state, addressError: updateAddressError.message };
      });
    }
  }, [setDrawerState, updateAddressError]);

  useEffect(() => {
    if (updateContactPointError) {
      setDrawerState((state) => {
        return { ...state, organizationError: updateContactPointError.message };
      });
    }
  }, [setDrawerState, updateContactPointError]);

  useEffect(() => {
    if (updateOrganizationError) {
      setDrawerState((state) => {
        return { ...state, organizationError: updateOrganizationError.message };
      });
    }
  }, [setDrawerState, updateOrganizationError]);

  useEffect(() => {
    if (state.organizationError || state.addressError || state.contactPointError) {
      scrollToTop();
    }
  }, [scrollToTop, state.addressError, state.contactPointError, state.organizationError]);

  return (
    <Drawer open={state.isOpen} onClose={handelClose}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content" overflow="auto">
        <Header attached="drawer">基本情報</Header>
        <Box overflow="auto" ref={containerRef} mb={theme.space.xl}>
          {(state.addressError || state.contactPointError || state.organizationError) && (
            <Alert status="error" m={`0 ${theme.space.xl} ${theme.space.m}`}>
              {state.addressError}
              {state.contactPointError}
              {state.organizationError}
            </Alert>
          )}
          <Box overflow="auto">
            <BaseForm
              ref={formRef}
              initialValue={initialValue}
              loading={loading}
              slug={organization?.slug || ''}
              pharmacyCodeEditDisabled={!!organization?.yakumaruCustomSettingActive}
              onClickGeocoding={handleClickGeocoding}
              onSubmit={handleSubmit}
              uberSettingEnable={uberSettingEnable}
              availableUber={availableUber}
            />
          </Box>
        </Box>
        <Drawer.Footer>
          <Button
            wide="fill"
            use="base"
            size="l"
            loading={loading}
            disabled={disabled}
            onClick={handleSend}
          >
            保存
          </Button>
        </Drawer.Footer>
      </Grid>
    </Drawer>
  );
};
